import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { getMonthLabel, getNextMonth, getPrevMonth, formatDateShort } from '../../utils/dates';
import { groupOverlapsByCity } from '../../utils/overlapGroups';
import { MonthGrid } from './MonthGrid';
import { FlightEntryModal } from '../flight-entry/FlightEntryModal';
import { ChevronLeft, ChevronRight, Plus, Crosshair, Upload, X } from 'lucide-react';
import { haptic, hapticMedium } from '../../utils/haptics';
import { CalendarSkeleton } from '../shared/SkeletonScreens';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import type { Flight } from '../../types';

const MISSING_TIMES_DISMISSED_PREFIX = 'rostfound_missing_times_dismissed_';
const FLIGHT_ENTRY_OPEN_KEY = 'rostfound-flight-entry-open';

export function CalendarTab() {
  const { loading, currentUser, flights, roster, overlaps, allFlights, month, year, setMonth, rosterActive, premiumGatedOverlap, pendingSharedImage, pendingSharedText } = useApp();
  const [showFlightEntry, setShowFlightEntry] = useState(
    () => localStorage.getItem(FLIGHT_ENTRY_OPEN_KEY) === '1' && localStorage.getItem('rostfound-roster-import') !== null
  );
  const [editingFlight, setEditingFlight] = useState<Flight | null>(null);
  const [transitionKey, setTransitionKey] = useState(0);
  const swipeDir = useRef<'left' | 'right'>('right');
  const [animating, setAnimating] = useState(false);
  // Compute city overlap groups (moved from OverlapsTab)
  const cityGroups = useMemo(
    () => loading ? [] : groupOverlapsByCity(currentUser, flights, allFlights, overlaps, month, year),
    [loading, currentUser, flights, allFlights, overlaps, month, year]
  );

  const handlePrev = () => {
    haptic();
    swipeDir.current = 'right';
    setAnimating(true);
    const { month: m, year: y } = getPrevMonth(year, month);
    setMonth(m, y);
    setTransitionKey((k) => k + 1);
  };

  const handleNext = () => {
    haptic();
    swipeDir.current = 'left';
    setAnimating(true);
    const { month: m, year: y } = getNextMonth(year, month);
    setMonth(m, y);
    setTransitionKey((k) => k + 1);
  };

  const handleToday = () => {
    haptic();
    setAnimating(true);
    const now = new Date();
    setMonth(now.getMonth() + 1, now.getFullYear());
    setTransitionKey((k) => k + 1);
  };

  const onAnimationEnd = useCallback(() => setAnimating(false), []);

  // Persist flight entry open state to localStorage (survives WKWebView process kill)
  useEffect(() => {
    if (showFlightEntry) {
      localStorage.setItem(FLIGHT_ENTRY_OPEN_KEY, '1');
    } else {
      localStorage.removeItem(FLIGHT_ENTRY_OPEN_KEY);
    }
  }, [showFlightEntry]);

  // Listen for notification-triggered roster import
  useEffect(() => {
    const handler = () => setShowFlightEntry(true);
    window.addEventListener('open-roster-import', handler);
    return () => window.removeEventListener('open-roster-import', handler);
  }, []);

  // Listen for shared roster text/images from iOS share extension
  useEffect(() => {
    const handler = () => setShowFlightEntry(true);
    window.addEventListener('shared-roster-text-ready', handler);
    window.addEventListener('shared-roster-image-ready', handler);
    return () => {
      window.removeEventListener('shared-roster-text-ready', handler);
      window.removeEventListener('shared-roster-image-ready', handler);
    };
  }, []);

  // On mount: open FlightEntryModal if shared data arrived while tab was unmounted
  useEffect(() => {
    if (pendingSharedImage || pendingSharedText) {
      setShowFlightEntry(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isCurrentMonth =
    month === new Date().getMonth() + 1 && year === new Date().getFullYear();

  const hasFlightsThisMonth = roster.some(e => e.type === 'flight' || e.type === 'layover');
  const nextM = month === 12 ? 1 : month + 1;
  const nextY = month === 12 ? year + 1 : year;
  const hasFlightsNextMonth = allFlights.some(f => {
    const d = new Date(f.date + 'T00:00:00');
    return d.getMonth() + 1 === nextM && d.getFullYear() === nextY;
  });
  const showEmptyPrompt = !hasFlightsThisMonth && !hasFlightsNextMonth;

  // Missing flight times nudge — once per roster month
  const monthKey = `${year}-${String(month).padStart(2, '0')}`;
  const missingTimesKey = `${MISSING_TIMES_DISMISSED_PREFIX}${monthKey}`;
  const [missingTimesDismissed, setMissingTimesDismissed] = useState(
    () => localStorage.getItem(missingTimesKey) === '1'
  );
  const myMonthFlights = flights.filter(f => {
    const d = new Date(f.date + 'T00:00:00');
    return d.getMonth() + 1 === month && d.getFullYear() === year;
  });
  const missingTimesCount = myMonthFlights.filter(f => !f.departureTime || !f.arrivalTime).length;
  const showMissingTimesNudge = missingTimesCount > 0 && !missingTimesDismissed && hasFlightsThisMonth;
  const dismissMissingTimes = () => {
    localStorage.setItem(missingTimesKey, '1');
    setMissingTimesDismissed(true);
  };

  const swipe = useSwipeNavigation(handleNext, handlePrev);

  // Always render the modal even while loading — it uses createPortal and
  // needs to stay mounted so restored import state doesn't flash
  const modal = (
    <FlightEntryModal
      open={showFlightEntry || !!editingFlight}
      onClose={() => { setShowFlightEntry(false); setEditingFlight(null); }}
      editFlight={editingFlight ?? undefined}
      calendarMonth={month}
      calendarYear={year}
    />
  );

  if (loading) return <><CalendarSkeleton />{modal}</>;

  return (
    <div className="pb-4" {...swipe}>
      {/* Month navigation */}
      <div className="sticky top-0 z-10 bg-slate-800/90 backdrop-blur-sm px-3 py-2 flex items-center justify-between border-b border-slate-700/50">
        <div className="flex items-center gap-0.5 w-16">
          <button data-testid="month-prev" onClick={handlePrev} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 active:scale-95 transition-transform">
            <ChevronLeft size={20} />
          </button>
          <button data-testid="month-next" onClick={handleNext} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 active:scale-95 transition-transform">
            <ChevronRight size={20} />
          </button>
        </div>

        <h2 className="text-sm font-semibold text-slate-100">{getMonthLabel(year, month)}</h2>

        <div className="flex items-center justify-end w-16">
          {!isCurrentMonth && (
            <button
              data-testid="month-today"
              onClick={handleToday}
              className="p-1 rounded-lg hover:bg-slate-700 text-accent active:scale-95 transition-transform"
              title="Go to today"
            >
              <Crosshair size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Grid with directional slide */}
      <div key={transitionKey} className={animating ? (swipeDir.current === 'left' ? 'animate-slide-in-right' : 'animate-slide-in-left') : ''} onAnimationEnd={onAnimationEnd}>
        <MonthGrid
          roster={roster}
          overlaps={overlaps}
          year={year}
          month={month}
          onEditFlight={setEditingFlight}
          cityGroups={cityGroups}
          currentUser={currentUser}
          rosterActive={rosterActive}
          premiumGatedOverlap={premiumGatedOverlap}
          externalSelectedDate={null}
          onExternalDateConsumed={() => {}}
        />
      </div>

      {/* Missing flight times nudge — once per roster month */}
      {showMissingTimesNudge && (
        <div className="mx-3 mt-2 rounded-lg bg-slate-800/80 border border-slate-700/60 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700/40">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              Missing flight times
            </span>
            <button onClick={dismissMissingTimes} className="text-slate-600 hover:text-slate-400 p-0.5">
              <X size={12} />
            </button>
          </div>
          <div className="px-3 py-1.5 flex flex-col gap-1">
            {myMonthFlights
              .filter(f => !f.departureTime || !f.arrivalTime)
              .map(f => (
                <div key={f.id} className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-slate-400">
                    {f.departureAirport} → {f.arrivalAirport}
                  </span>
                  <span className="text-slate-600">{formatDateShort(f.date)}</span>
                  {f.flightNumber && <span className="text-slate-600">{f.flightNumber}</span>}
                  <button
                    onClick={() => { setEditingFlight(f); dismissMissingTimes(); }}
                    className="ml-auto text-accent text-[11px] font-medium active:opacity-70"
                  >
                    Add times
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Empty month prompt */}
      {showEmptyPrompt && !loading && (
        <button
          onClick={() => { haptic(); setShowFlightEntry(true); }}
          className="mx-3 mt-2 p-4 rounded-xl border border-dashed border-slate-600 bg-slate-800/50 flex flex-col items-center gap-2 active:bg-slate-700/50 transition-colors"
        >
          <Upload size={20} className="text-slate-500" />
          <span className="text-sm text-slate-400 text-center">
            No flights this month — tap to import your schedule
          </span>
        </button>
      )}

      {/* FAB */}
      <button
        data-tour="fab-add"
        onClick={() => { hapticMedium(); setShowFlightEntry(true); }}
        className="sticky bottom-4 float-right mr-4 w-14 h-14 rounded-full shadow-lg shadow-accent/20 flex items-center justify-center text-white z-20 bg-accent dark:bg-accent-dark hover:bg-accent-dark active:scale-90 transition-transform"
      >
        <Plus size={24} />
      </button>

      {modal}
    </div>
  );
}

